import type { SimulationLinkDatum, SimulationNodeDatum } from 'd3-force';
import {
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
} from 'd3-force';

import { FORCE_CONFIG } from './householdLoadingGraph.config';
import type {
  GraphCompositionEdge,
  GraphPoint,
  GraphRole,
  GraphSpace,
} from './householdLoadingGraph.types';

const {
  REPULSION,
  GRAVITY,
  DAMPING,
  REPULSION_RANGE,
  SETTLE_STEPS,
  SEED_DISTANCE,
  FRAME_PADDING,
  MIN_FRAME_EXTENT,
  MAX_FRAME_SCALE,
  CHARGE,
  SPRINGS,
} = FORCE_CONFIG;
// Mathematical constant used to seed nodes at evenly spaced angles.
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

type Particle = SimulationNodeDatum &
  GraphPoint & {
    id: string;
    charge: number;
  };

type Spring = SimulationLinkDatum<Particle> & {
  length: number;
  strength: number;
};

/**
 * Settle repulsion and spring forces from retained, unscaled coordinates.
 * Fit each axis to the frame; graph distances do not encode factual values.
 * Cap expansion so one or two early nodes do not stretch across the frame.
 * The renderer eases toward these
 * targets, keeping labels and edge endpoints attached throughout the move.
 */
export function householdLoadingForceLayout(
  ids: readonly string[],
  edges: readonly GraphCompositionEdge[],
  roles: ReadonlyMap<string, GraphRole>,
  space: GraphSpace,
  previous?: ReadonlyMap<string, GraphPoint>
) {
  const particles = new Map<string, Particle>();
  for (const [index, id] of ids.entries()) {
    const role = roles.get(id)!;
    const angle = index * GOLDEN_ANGLE;
    const neighbor = edges
      .flatMap((edge) => {
        if (edge.sourceID === id) return [edge.targetID];
        if (edge.targetID === id) return [edge.sourceID];
        return [];
      })
      .find((other) => previous?.has(other) || particles.has(other));
    const anchor = neighbor
      ? (previous?.get(neighbor) ?? particles.get(neighbor))
      : undefined;
    const distance = anchor
      ? SEED_DISTANCE
      : SEED_DISTANCE * Math.sqrt(index + 1);
    const retained = previous?.get(id);
    particles.set(id, {
      x: retained?.x ?? (anchor?.x ?? 0) + Math.cos(angle) * distance,
      y: retained?.y ?? (anchor?.y ?? 0) + Math.sin(angle) * distance,
      id,
      charge: CHARGE[role],
    });
  }
  const all = [...particles.values()];
  const springs: Spring[] = edges.map((edge) => {
    const aHub = roles.get(edge.sourceID) === 'hub';
    const bHub = roles.get(edge.targetID) === 'hub';
    let spring = SPRINGS.leaf;
    if (aHub || bHub) spring = SPRINGS.branch;
    if (aHub && bHub) spring = SPRINGS.hub;
    if (edge.curve === 'rim') spring = SPRINGS.cross;
    return {
      source: edge.sourceID,
      target: edge.targetID,
      length: spring.length,
      strength: spring.strength,
    };
  });
  forceSimulation(all)
    .alphaDecay(0)
    .velocityDecay(1 - DAMPING)
    .force(
      'charge',
      forceManyBody<Particle>()
        .strength((particle) => -REPULSION * particle.charge)
        .distanceMax(REPULSION_RANGE)
    )
    .force(
      'link',
      forceLink<Particle, Spring>(springs)
        .id((particle) => particle.id)
        .distance((spring) => spring.length)
        .strength((spring) => spring.strength)
    )
    .force('x', forceX<Particle>(0).strength(GRAVITY))
    .force('y', forceY<Particle>(0).strength(GRAVITY))
    .stop()
    .tick(SETTLE_STEPS);
  const simulationPoints = new Map(
    ids.map((id) => {
      const { x, y } = particles.get(id)!;
      return [id, { x, y }] as const;
    })
  );
  const points = new Map<string, GraphPoint>();
  if (!all.length) return { simulationPoints, points };
  const minX = Math.min(...all.map((node) => node.x));
  const maxX = Math.max(...all.map((node) => node.x));
  const minY = Math.min(...all.map((node) => node.y));
  const maxY = Math.max(...all.map((node) => node.y));
  const scaleX = Math.min(
    (space.width - FRAME_PADDING * 2) / Math.max(maxX - minX, MIN_FRAME_EXTENT),
    MAX_FRAME_SCALE
  );
  const scaleY = Math.min(
    (space.height - FRAME_PADDING * 2) /
      Math.max(maxY - minY, MIN_FRAME_EXTENT),
    MAX_FRAME_SCALE
  );
  for (const [id, node] of simulationPoints) {
    points.set(id, {
      x: (node.x - (minX + maxX) / 2) * scaleX + space.width / 2,
      y: (node.y - (minY + maxY) / 2) * scaleY + space.height / 2,
    });
  }
  return { simulationPoints, points };
}
